import { Component, OnInit, inject, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilderComponent } from '../../components/form-builder/form-builder.component';
import { DynamicForm } from '../../components/form-builder/form-builder.types';
import { PageHeaderComponent } from '../../components/shared/page-header/page-header.component';
import { ButtonModule } from 'primeng/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ProfileService, ProfileResponse } from './profile.service';
import { DialogComponent } from '../../components/dialog/dialog.component';
import { DialogObject } from '../../components/dialog/dialog.types';
import { Router } from '@angular/router';
import { SpinnerComponent } from '../../components/shared/spinner/spinner.component';
import { getProfileFormObject } from './utils/profile-form-object';
import { getPasswordFormObject } from './utils/password-form-object';
import Keycloak from 'keycloak-js';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DataTableBuilderComponent } from '../../components/data-table-builder/data-table-builder.component';
import { TabsModule } from 'primeng/tabs';
import { DataTableColumnVariant, DynamicDataTable, DataTableVariant } from '../../components/data-table-builder/data-table.types';

@Component({
  selector: 'sm-profile',
  standalone: true,
  imports: [
    CommonModule,
    FormBuilderComponent,
    PageHeaderComponent,
    ButtonModule,
    TranslateModule,
    DialogComponent,
    SpinnerComponent,
    ReactiveFormsModule,
    DataTableBuilderComponent,
    TabsModule,
  ],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss',
})
export class ProfileComponent implements OnInit, AfterViewInit {
  private profileService = inject(ProfileService);
  private messageService = inject(MessageService);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private keycloak = inject(Keycloak);

  @ViewChild(FormBuilderComponent) formBuilderComponent!: FormBuilderComponent;
  @ViewChild('dialogComponent') dialogComponent!: DialogComponent;

  loading = false;
  profileData: ProfileResponse | null = null;
  dialogVisible = false;
  userDialog?: DialogObject;

  fullName = '';
  email = '';

  passwordForm = new FormGroup({
    newPassword: new FormControl('', [Validators.required, Validators.minLength(6)]),
    confirmPassword: new FormControl('', [Validators.required]),
  });
  passwordDialogVisible = false;
  passwordDialog?: DialogObject;

  passwordFormObject: DynamicForm = {
    title: '',
    description: '',
    isHeaderHidden: false,
    grids: [],
    orientation: 'vertical',
  };

  formObject: DynamicForm = {
    title: '',
    description: '',
    class: '',
    grids: [],
    orientation: 'vertical',
  };

  breadcrumb = [
    {
      label: 'profile.breadcrumb.home',
      routerLink: ['/'],
    },
    {
      label: 'profile.breadcrumb.profile',
      routerLink: ['/profile'],
    },
  ];

  selectedTab = '0';
  rolesTableObject: DynamicDataTable = {
    singular: 'settings.userInfo.table.role',
    plural: 'settings.userInfo.table.roles',
    columns: [{ label: 'settings.userInfo.table.role', accessorKey: 'role', variant: DataTableColumnVariant.TEXT }],
    variant: DataTableVariant.COMMON,
    sizes: [10],
    stripedRows: true,
    searchableFields: ['role'],
  };
  acsTableObject: DynamicDataTable = {
    singular: 'settings.userInfo.table.ac',
    plural: 'settings.userInfo.table.acs',
    columns: [
      { label: 'settings.userInfo.table.nom', accessorKey: 'nom', variant: DataTableColumnVariant.TEXT },
      { label: 'settings.userInfo.table.ancrage', accessorKey: 'ancrage', variant: DataTableColumnVariant.TEXT },
    ],
    variant: DataTableVariant.COMMON,
    sizes: [10],
    stripedRows: true,
    searchableFields: ['nom', 'ancrage'],
  };
  comsTableObject: DynamicDataTable = {
    singular: 'settings.userInfo.table.com',
    plural: 'settings.userInfo.table.coms',
    columns: [{ label: 'settings.userInfo.table.nom', accessorKey: 'nom', variant: DataTableColumnVariant.TEXT }],
    variant: DataTableVariant.COMMON,
    sizes: [10],
    stripedRows: true,
    searchableFields: ['nom'],
  };
  userRolesTableData: any[] = [];
  userAcs: any[] = [];
  userComs: any[] = [];

  private initialFormValues: any = null;
  hasFormChanges = false;

  ngOnInit(): void {
    this.loadProfileData();
  }

  ngAfterViewInit(): void {
    this.passwordFormObject = getPasswordFormObject(this.passwordForm, this.translate);
  }

  loadProfileData(): void {
    this.loading = true;
    this.profileService.getProfile().subscribe({
      next: data => {
        this.profileData = data;
        this.fullName = `${data.firstName || ''} ${data.lastName || ''}`.trim();
        this.email = data.email || '';
        this.initializeForm();
        this.userRolesTableData = Array.isArray(data.roles) ? data.roles.map((role: string) => ({ role })) : [];
        this.userAcs = Array.isArray(data.acs) ? data.acs : [];
        this.userComs = Array.isArray(data.coms) ? data.coms : [];
        this.loading = false;
        this.initialFormValues = JSON.parse(JSON.stringify(data)); // Deep copy for change detection
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('profile.message.error.loading'),
        });
        this.loading = false;
      },
    });
  }

  initializeForm(): void {
    this.formObject = getProfileFormObject({
      profileData: this.profileData,
      onSubmit: () => this.updateProfile(),
    });
  }

  updateProfile(): void {
    this.userDialog = {
      title: this.translate.instant('profile.update.confirmTitle'),
      description: this.translate.instant('profile.update.confirmDescription'),
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onHide: () => {},
      actions: [
        {
          label: this.translate.instant('common.confirm'),
          variant: 'success',
          onClick: () => {
            this.dialogVisible = false;
            this.performUpdate();
          },
        },
        {
          label: this.translate.instant('common.cancel'),
          variant: 'secondary',
          onClick: () => {
            this.dialogVisible = false;
          },
        },
      ],
    };
    this.dialogVisible = true;
  }

  private performUpdate(): void {
    if (!this.profileData) return;

    const updateRequest = {
      email: this.profileData.email,
      firstName: this.profileData.firstName,
      lastName: this.profileData.lastName,
      address: this.profileData.address,
      mobile: this.profileData.mobile,
      fixe: this.profileData.fixe,
    };

    this.loading = true;
    this.profileService.updateProfile(updateRequest).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('profile.message.success.updated'),
        });
        this.loadProfileData();
        this.loading = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('profile.message.error.updated'),
        });
        this.loading = false;
      },
    });
  }

  resetForm(): void {
    this.initializeForm();
  }

  logout(): void {
    this.keycloak.logout();
  }

  openPasswordDialog() {
    this.passwordForm.markAllAsTouched();
    this.passwordForm.updateValueAndValidity();

    if (this.passwordForm.valid) {
      this.passwordDialog = {
        title: this.translate.instant('profile.form.password.confirmTitle'),
        description: this.translate.instant('profile.form.password.confirmDescription'),
        onHide: () => {
          this.passwordDialogVisible = false;
        },
        actions: [
          {
            label: this.translate.instant('common.confirm'),
            variant: 'success',
            onClick: () => {
              this.passwordDialogVisible = false;
              this.resetPassword();
            },
          },
          {
            label: this.translate.instant('common.cancel'),
            variant: 'secondary',
            onClick: () => {
              this.passwordDialogVisible = false;
            },
          },
        ],
      };
      this.passwordDialogVisible = true;
    }
  }

  resetPassword() {
    if (this.passwordForm.invalid) {
      return;
    }

    const { newPassword, confirmPassword } = this.passwordForm.value;

    this.loading = true;
    this.profileService
      .resetPassword({
        newPassword: newPassword ?? '',
        confirmPassword: confirmPassword ?? '',
      })
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('profile.form.password.success'),
          });
          this.passwordForm.reset();
          this.loading = false;
        },
        error: error => {
          const errorMessage = error.error?.message || error.message || 'Unknown error';
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('profile.form.password.error'),
            detail: errorMessage,
          });
          this.loading = false;
        },
      });
  }

  checkFormChanges() {
    this.hasFormChanges = JSON.stringify(this.profileData) !== JSON.stringify(this.initialFormValues);
  }

  cancelEdit() {
    this.checkFormChanges();
    if (this.hasFormChanges) {
      this.userDialog = {
        title: this.translate.instant('profile.update.confirmationDialog.title'),
        description: this.translate.instant('profile.update.confirmationDialog.description'),
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onHide: () => {},
        actions: [
          {
            label: this.translate.instant('profile.update.confirmationDialog.yes'),
            variant: 'success',
            onClick: () => {
              this.dialogVisible = false;
              this.profileData = JSON.parse(JSON.stringify(this.initialFormValues));
              this.initializeForm();
            },
          },
          {
            label: this.translate.instant('profile.update.confirmationDialog.no'),
            variant: 'secondary',
            onClick: () => {
              this.dialogVisible = false;
            },
          },
        ],
      };
      this.dialogVisible = true;
    } else {
      this.profileData = JSON.parse(JSON.stringify(this.initialFormValues));
      this.initializeForm();
    }
  }
}
